import React, { FC, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { Plural } from '@lingui/macro'

import EventFormattedTime from '../../../../../../shared/components/event-formatted-time/EventFormattedTime'
import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from '../../../../app/util/appHistory'
import { Card } from '../../../../components/common/card/Card'
import CardTitle from '../../../../components/common/card/CardTitle'
import CardBody from '../../../../components/common/card/CardBody'
import Tags from '../../../../components/common/tag/Tags'
import DateIcon from '../date-icon/DateIcon'
import { useEventsTimezoneQuery } from './EventCard.codegen'
import { UpcomingEventFragment } from '../upcoming-events-row/UpcomingEventsRow.codegen'
import { Box, Flex, Link, Text } from 'front-end/src/components'

import './EventCard.less'
import { getQuillStyle } from 'front-end/src/components/common/quill/EditorQuill'
import { formatEventLocation } from '../../eventUtils'
import IconClock from 'front-end/src/components/common/icon/IconClock'
import IconGlobe from 'front-end/src/components/common/icon/IconGlobe'
import IconLocationPin from 'front-end/src/components/common/icon/IconLocationPin'
import { isUrl } from '../../../../utils/url'
import { StripHTMLTags } from 'front-end/src/utils/DangerouslySetInnerHTML'
import { getTimeZoneDateFromUTCDate } from 'front-end/src/pages/manage-events/event-edit/tabs/event-detail/eventDetailUtils'
import IconRefresh from 'front-end/src/components/common/icon/IconRefresh'
import { RecurrenceText } from 'front-end/src/pages/manage-events/components/recurrence-text/RecurrenceText'
import { css } from '@emotion/react'

export type EventWithAttendeesCount = UpcomingEventFragment & {
  attendeesCount: number
}

export interface EventCardProps {
  event: EventWithAttendeesCount & {
    eventTitle?: string
  }
}

export const EventCard: FC<EventCardProps> = ({ event }) => {
  const { data: eventsTimezone } = useEventsTimezoneQuery()
  const descriptionRef = useRef<HTMLDivElement>(null)
  const showDescriptionDots = useMemo(() => {
    const el = descriptionRef.current
    return !!el && el.scrollHeight > el.clientHeight
  }, [descriptionRef.current])

  let placesLeft
  if (event.participants_limit && event.participants_limit > 0) {
    placesLeft = event.participants_limit - event.attendeesCount
  }
  const location = formatEventLocation(event.location)
  const placesLeftAbsolute = placesLeft > 0 ? placesLeft : 0

  const subtitle = (
    <>
      {event.recurringEvent?.recurrence && (
        <Link
          // card is already <a> it throw warning to console
          as="span"
          display="flex"
          alignItems="center"
          mb="4px"
          css={css`
            &:hover {
              text-decoration: none;
              .chakra-text {
                text-decoration: underline;
              }
            }
          `}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (event.recurringEvent?.id) {
              navigateTo(linkBuilder.eventDetailSeries(event.recurringEvent.id))
            }
          }}
        >
          <IconRefresh mr="8px" />
          <Text>
            <RecurrenceText recurrence={event.recurringEvent.recurrence} />
          </Text>
        </Link>
      )}
      <Flex>
        <IconClock mr="8px" pt="3px" />
        <EventFormattedTime
          event={event}
          timezone={eventsTimezone?.me.timezone}
        />
      </Flex>
      {location && (
        <Flex className="Card__location">
          {isUrl(location) ? (
            <IconGlobe pt="3px" mr="8px" />
          ) : (
            <IconLocationPin pt="3px" mr="8px" />
          )}
          {location}
        </Flex>
      )}
      {placesLeft <= 5 && (
        <Box className="Card__places-left" mt="4px">
          <span className="danger">
            <Plural
              value={placesLeftAbsolute}
              _0="No places left"
              one="# place remaining"
              other="# places remaining"
            />
          </span>
        </Box>
      )}
    </>
  )

  const classes = classNames('EventCard')
  const eventStartTime = getTimeZoneDateFromUTCDate({
    date: event.start_time,
    timezone: eventsTimezone?.me.timezone
  })

  return (
    <Card className={classes} linkUrl={linkBuilder.eventDetail(event.id)}>
      <Box p="16px 16px 2px">
        {eventStartTime && <DateIcon date={eventStartTime} />}
      </Box>
      <CardTitle title={event.title || event.eventTitle} subtitle={subtitle} />

      <div
        className={classNames('EventCard__description-wrapper', {
          ellipsis: showDescriptionDots
        })}
      >
        <CardBody>
          <Box
            css={getQuillStyle('EventCard__description')}
            className="EventCard__description"
            dangerouslySetInnerHTML={{
              __html: StripHTMLTags(event.description || '')
            }}
            ref={descriptionRef}
          />
        </CardBody>
      </div>
      <div className="EventCard__tags">
        <Tags
          items={event.tags}
          size="small"
          onClick={(eventTag) => {
            navigateTo(linkBuilder.eventTag(eventTag.id))
          }}
        />
      </div>
    </Card>
  )
}

export default EventCard
